import {Component, NgZone, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {OutlineHandlerService} from "./outline-handler.service";
import {IOutline, ISection} from "./outline.interface";
import {DragulaService} from "ng2-dragula";
import {NotifierComponent} from "../shared/components/notifier/notifier.component";
import {Notification} from "../shared/components/notifier/notification";
import {NotificationType} from "../shared/components/notifier/notification-type";
import {DbValidators} from "../shared/validators/DbValidators";
import {ActivatedRoute} from "@angular/router";

@Component({
    selector: 'db-outline-handler',
    templateUrl: './outline-handler.component.html',
    styleUrls: ['./outline-handler.component.scss'],
    standalone: false
})
export class OutlineHandlerComponent implements OnInit {
  generationForm: FormGroup = this._fb.group({
    description: ['', Validators.required],
    business_type: ['', Validators.required],
    custom_fields: this._fb.array([])
  });
  sections: ISection[] = [];
  outline: IOutline;
  pageOutlineForm: FormGroup = this._fb.group({
    sections: this._fb.array([]),
  });
  loading = false;
  fileUrl;
  testMode = false;
  generateImages = false;

  constructor(
    private ngZone: NgZone,
    private _fb: FormBuilder,
    public dragulaService: DragulaService,
    private _route: ActivatedRoute,
    private _outlineHandlerService: OutlineHandlerService
  ) {
    this.dragulaService.createGroup('sections', {
      revertOnSpill: true,
      direction: 'vertical',
    });
  }

  ngOnInit(): void {
  }

  get sectionsFormArray(): FormArray {
    return this.pageOutlineForm.get('sections') as FormArray;
  }

  get generationFormArray(): FormArray {
    return this.generationForm.get('custom_fields') as FormArray;
  }

  get outlineFormArray(): FormArray {
    return this.pageOutlineForm.get('custom_fields') as FormArray;
  }

  generateOutline() {
    if (this.generationForm.invalid) {
      return;
    }
    const data = this._getGenerationFormData();
    this.loading = true;
    this._outlineHandlerService.generateOutline(data, this.testMode)
      .subscribe((response: { data: IOutline, status: string, url: string }) => {
        this.outline = response.data;
        this.sections = response.data.page_outline.sections;
        this._initSectionsForm(response.data.page_outline.sections);
        this.loading = false;
      }, () => {
        this.loading = false;
        NotifierComponent.notify(new Notification("Something went wrong", NotificationType.DANGER));
      });
  }

  private _getGenerationFormData() {
    const formData = this.generationForm.getRawValue();
    const data = {
      ...formData,
      ...this._reduceCustomFields(formData)
    };
    delete data.custom_fields;
    return data;
  }

  private _reduceCustomFields(formData) {
    return formData.custom_fields.reduce((acc, {field_key, field_value}) => {
      acc[field_key] = field_value;
      return acc;
    }, {});
  }

  private _initSectionsForm(initialSections: ISection[]) {
    initialSections.forEach((section, index) => {
      this.sectionsFormArray.push(this._createSectionFormGroup(section));
    });
  }

  private _createSectionFormGroup(section: ISection) {
    return this._fb.group({
      section_description: [section.section_description],
      section_title: [section.section_title],
      section_type: [section.section_type],
      custom_fields: this._fb.array([]),
      index: [section.index]
    });
  }

  private _createCustomFormGroup() {
    return this._fb.group({
      field_key: ['', [Validators.required, DbValidators.trim]],
      field_value: ''
    });
  }

  addGenerationField() {
    this.generationFormArray.push(this._createCustomFormGroup());
  }

  removeGenerationField(index: number) {
    this.generationFormArray.removeAt(index);
  }

  removeSection(index: number) {
    this.sectionsFormArray.removeAt(index);
    this.sections.splice(index, 1);
  }

  addSection() {
    const data = {
      section_description: '',
      section_title: '',
      section_type: '',
      index: this.sectionsFormArray.controls.length,
    };
    this.sectionsFormArray.push(this._createSectionFormGroup(data));
    this.sections.push(data);
  }

  addSectionField(i) {
    (this.sectionsFormArray.at(i).get('custom_fields') as FormArray).push(this._createCustomFormGroup());
  }

  removeSectionField(i, parentIndex) {
    (this.sectionsFormArray.at(parentIndex).get('custom_fields') as FormArray).removeAt(i);
  }

  saveOutline() {
    const sections = [];
    this.sectionsFormArray.controls.forEach((form: FormGroup) => {
      const formValue = form.getRawValue();
      const foundIndex = this.sections.findIndex((item: ISection) => item.index === formValue.index);
      sections[foundIndex] = {
        ...this.sections[foundIndex],
        section_title: formValue.section_title,
        section_description: formValue.section_description,
        section_type: formValue.section_type,
        index: foundIndex,
        ...this._reduceCustomFields(formValue)
      };
      delete sections[foundIndex].custom_fields;
    });
    this.outline.page_outline.sections = sections;
    const generationFormValue = this.generationForm.getRawValue();
    const data = {
      ...this.outline,
      generate_images: this.generateImages,
      description: generationFormValue.description,
      business_type: generationFormValue.business_type,
    };

    this.loading = true;
    this._outlineHandlerService.saveOutline(data, this.testMode).subscribe((response: any) => {
      NotifierComponent.notify(new Notification("Outline generated", NotificationType.SUCCESS));
      this.fileUrl = response.data[0];
      this._sendFileUrl(this.fileUrl);
      this.loading = false;
    }, () => {
      this.fileUrl = null;
      this.loading = false;
      NotifierComponent.notify(new Notification("Something went wrong", NotificationType.DANGER));
    });
  }

  private _sendFileUrl(url: string) {
    this._outlineHandlerService.sendUrl(url, this._route.snapshot.parent.data['website'].id)
      .subscribe(resp => {
      });
  }

}
